import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Users2, Plus, LogIn } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Host-managed breakout rooms — each breakout is a separate LiveKit room.
 * Participants pick which room to join; `onSwitchRoom(breakoutId|null)` tears
 * down the current LiveKit connection and re-connects to the chosen room.
 * `httpClient` defaults to the authenticated `api` instance but can be swapped
 * for `guestApi` when rendered for an unauthenticated meeting attendee. */
export function BreakoutRoomsPanel({ meetingId, isHost, activeBreakoutId, onSwitchRoom, onClose, httpClient = api, liveBreakoutRooms }) {
  const [breakouts, setBreakouts] = useState([]);
  const [count, setCount] = useState(2);
  const [autoAssign, setAutoAssign] = useState(true);
  const [loading, setLoading] = useState(false);

  const load = () => {
    httpClient.get(`/meetings/${meetingId}/breakout-rooms`).then(({ data }) => setBreakouts(data.breakout_rooms || [])).catch(() => {});
  };

  useEffect(() => { load(); }, [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live server push (host created/closed rooms while this panel was already open) —
  // keeps the list in sync without needing to close and reopen the modal.
  useEffect(() => {
    if (liveBreakoutRooms !== undefined) setBreakouts(liveBreakoutRooms);
  }, [liveBreakoutRooms]);

  const createBreakouts = async () => {
    setLoading(true);
    try {
      const { data } = await httpClient.post(`/meetings/${meetingId}/breakout-rooms`, { count, auto_assign: autoAssign });
      setBreakouts(data.breakout_rooms || []);
      toast.success(autoAssign ? `${data.breakout_rooms.length} breakout rooms created — participants auto-assigned` : `${data.breakout_rooms.length} breakout rooms created`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create breakout rooms");
    } finally {
      setLoading(false);
    }
  };

  const closeAll = async () => {
    setLoading(true);
    try {
      await httpClient.delete(`/meetings/${meetingId}/breakout-rooms`);
      setBreakouts([]);
      if (activeBreakoutId) onSwitchRoom(null);
      toast.success("Breakout rooms closed — everyone is back in the main room");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not close breakout rooms");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] px-4" data-testid="breakout-rooms-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2"><Users2 className="w-5 h-5 text-fuchsia-400" /> Breakout Rooms</h3>
          <button onClick={onClose} data-testid="breakout-rooms-close-btn" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {isHost && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center gap-2">
              <input type="number" min={1} max={20} value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="breakout-rooms-count-input"
                className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white" />
              <button onClick={createBreakouts} disabled={loading} data-testid="breakout-rooms-create-btn"
                className="flex items-center gap-1.5 text-xs font-semibold bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 px-3 py-2 rounded-lg disabled:opacity-60">
                <Plus className="w-3.5 h-3.5" /> Create Rooms
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={autoAssign} onChange={(e) => setAutoAssign(e.target.checked)} data-testid="breakout-rooms-autoassign-toggle" className="accent-cyan-500" />
              Automatically assign participants evenly
            </label>
          </div>
        )}

        <div className="space-y-2 max-h-64 overflow-y-auto">
          <button onClick={() => onSwitchRoom(null)} data-testid="breakout-room-main-btn"
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${!activeBreakoutId ? "bg-cyan-500/20 text-cyan-300" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Main Room <LogIn className="w-4 h-4" />
          </button>
          {breakouts.map((b) => (
            <button key={b.id} onClick={() => onSwitchRoom(b.id)} data-testid={`breakout-room-${b.id}-btn`}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${activeBreakoutId === b.id ? "bg-cyan-500/20 text-cyan-300" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
              {b.title} <LogIn className="w-4 h-4" />
            </button>
          ))}
          {breakouts.length === 0 && <p className="text-xs text-slate-500 text-center py-4">No breakout rooms yet.</p>}
        </div>

        {isHost && breakouts.length > 0 && (
          <button onClick={closeAll} disabled={loading} data-testid="breakout-rooms-close-all-btn" className="mt-4 w-full text-xs font-semibold text-red-300 hover:text-red-200 disabled:opacity-60">
            {loading ? "Closing..." : "Close All Breakout Rooms"}
          </button>
        )}
      </div>
    </div>
  );
}
